// Transport for the score. Schedules melody + voiced-chord events on the audio
// clock with a lookahead window (sample-accurate, drift-free — the AudioContext
// clock is authoritative, not setTimeout), and drives a playhead via
// requestAnimationFrame. Degrades to a silent playhead sweep when there's no
// AudioContext (e.g. tests). Task #20.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getChordPlayer, type NoteExpressionOptions, type TransportState } from './player';
import { eventsDueBy, type Timeline } from '@/music/notation/timeline';
import {
  clipAuditionEvents,
  normalizeAuditionRange,
  type ScoreAuditionRange,
} from '@/music/notation/scoreAudition';
import { useStore } from '@/state/store';

export interface ScorePlayer {
  playing: boolean;
  transport: TransportState;
  /** Absolute timeline beat of the playhead (buildTimeline's unit), or null when stopped. */
  playheadBeat: number | null;
  /** Play the full written score, or an absolute interval within it. */
  play: (range?: ScoreAuditionRange) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

const LOOKAHEAD_S = 0.15; // schedule this far ahead of the audio clock
const SCHED_INTERVAL_MS = 25; // how often the lookahead loop runs
const MIN_SCHEDULE_LEAD_S = 0.01;

export function useScorePlayer(timeline: Timeline, bpm: number): ScorePlayer {
  const leadSound = useStore((s) => s.leadSound);
  const bassSound = useStore((s) => s.bassSound);
  const guitarSound = useStore((s) => s.guitarSound);
  const [transport, setTransport] = useState<TransportState>('idle');
  const transportRef = useRef<TransportState>('idle');
  const [playheadBeat, setPlayheadBeat] = useState<number | null>(null);
  const scheduler = useRef<ReturnType<typeof setInterval> | null>(null);
  const raf = useRef<number | null>(null);
  // Mirrors `playing` so stop() (also the unmount cleanup) can damp the shared
  // player only when the score was actually sounding — leaving an arrangement
  // the user parked on another page alone.
  const soundingRef = useRef(false);
  const ownerGenerationRef = useRef<number | null>(null);
  const playRequestRef = useRef(0);
  const sourceRef = useRef({ timeline, bpm });
  const pausedBeatRef = useRef(0);
  const rangeRef = useRef<ScoreAuditionRange | null>(null);
  const readBeatRef = useRef<(() => number) | null>(null);

  useEffect(() => {
    const player = getChordPlayer();
    player.setLeadSound(leadSound);
    player.setBassSound(bassSound);
    player.setGuitarSound(guitarSound);
  }, [leadSound, bassSound, guitarSound]);

  const finish = useCallback((stopOwnedAudio: boolean) => {
    playRequestRef.current += 1;
    if (scheduler.current !== null) clearInterval(scheduler.current);
    scheduler.current = null;
    if (raf.current !== null) cancelAnimationFrame(raf.current);
    raf.current = null;
    if (soundingRef.current && stopOwnedAudio) {
      // Silence for real: damp voices already dispatched inside the lookahead
      // window instead of letting them ring out their full envelopes. If a
      // newer transport already claimed the singleton, only retire our local
      // timers — stopping here would silence that newer owner.
      try {
        const player = getChordPlayer();
        const owner = ownerGenerationRef.current;
        if (owner === null || player.getPlaybackGeneration() === owner) player.stop();
      } catch {
        /* no audio available */
      }
    }
    soundingRef.current = false;
    ownerGenerationRef.current = null;
    transportRef.current = 'idle';
    pausedBeatRef.current = 0;
    rangeRef.current = null;
    readBeatRef.current = null;
    setTransport('idle');
    setPlayheadBeat(null);
  }, []);

  const stop = useCallback(() => finish(true), [finish]);

  const startFrom = useCallback(
    (fromBeat: number, range: ScoreAuditionRange) => {
      const offset = Math.max(range.fromBeat, Math.min(fromBeat, range.toBeat));
      if (offset >= range.toBeat) {
        stop();
        return;
      }
      // Retire any pending score request first. The shared-player stop below then
      // atomically claims audio from an arrangement or another score pass.
      finish(false);
      const request = ++playRequestRef.current;
      rangeRef.current = range;
      // Pausing damps dispatched notes. Resume the remaining part of a sustained
      // note, and omit everything that ended before the parked playhead.
      const events = clipAuditionEvents(timeline.events, {
        fromBeat: offset,
        toBeat: range.toBeat,
      });

      const spb = 60 / Math.max(20, bpm); // seconds per timeline beat
      const endSec = (range.toBeat - offset) * spb;

      // Prefer the audio clock; fall back to wall-clock (silent) if unavailable.
      let player: ReturnType<typeof getChordPlayer> | null = null;
      let claimedGeneration: number | null = null;
      try {
        player = getChordPlayer();
        // The player is a shared singleton with a single-scheduler contract: like
        // every other play entry, take it over — cancel a running arrangement
        // (and any prior score pass) instead of layering on top of it.
        player.stop();
        claimedGeneration = player.getPlaybackGeneration();
        ownerGenerationRef.current = claimedGeneration;
      } catch {
        player = null;
      }
      // This is logical transport ownership as well as audible output: keep the
      // silent wall-clock fallback alive when Web Audio is unavailable.
      soundingRef.current = true;
      transportRef.current = 'playing';
      setTransport('playing');
      // Show the starting/resume position immediately, even while the audio
      // context is still waking up.
      setPlayheadBeat(offset);
      readBeatRef.current = () => offset;

      // Anchor the clock only AFTER resume() resolves: iOS parks the context
      // suspended/interrupted and its currentTime is unreliable until it is
      // actually running — anchoring early made the playhead's first real paint
      // land mid-bar (the clock jumped during the ~0.3–1s resume).
      const begin = (audioStart: number, ownerGeneration: number | null) => {
        const perfStart = performance.now();
        const elapsed = () =>
          player ? player.now() - audioStart : (performance.now() - perfStart) / 1000;
        readBeatRef.current = () =>
          Math.min(range.toBeat, Math.max(offset, offset + elapsed() / spb));
        const superseded = () => {
          if (!soundingRef.current) return true;
          if (
            player &&
            ownerGeneration !== null &&
            player.getPlaybackGeneration() !== ownerGeneration
          ) {
            finish(false);
            return true;
          }
          return false;
        };

        let nextIndex = 0;
        if (player) {
          scheduler.current = setInterval(() => {
            if (superseded()) return;
            const audioNow = player!.now();
            const uptoBeat = offset + (audioNow + LOOKAHEAD_S - audioStart) / spb;
            const { due, nextIndex: ni } = eventsDueBy(events, nextIndex, uptoBeat);
            nextIndex = ni;
            for (const ev of due) {
              const when = audioStart + (ev.absBeat - offset) * spb;
              // A throttled/hidden main thread may return with several events in
              // the past. Replaying all of them at `audioNow` creates a burst;
              // advance the cursor but resume with the next genuinely future note.
              if (when < audioNow + MIN_SCHEDULE_LEAD_S) continue;
              const sound = ev.sound ?? (ev.kind === 'melody' ? 'lead' : 'guitar');
              const expressive = ev.velocity !== undefined || ev.articulationGate !== undefined || ev.expression !== undefined;
              const expression: NoteExpressionOptions = {
                ...(ev.velocity !== undefined ? { velocity: ev.velocity } : {}),
                ...(ev.articulationGate !== undefined ? { articulationGate: ev.articulationGate } : {}),
                ...(ev.expression ? { expression: ev.expression.map(({ beatOffset, ...point }) => ({
                  ...point, offsetSeconds: beatOffset * spb,
                })) } : {}),
              };
              for (const midi of ev.midis) {
                const duration = ev.durBeats * spb;
                const schedule = sound === 'piano' ? player!.schedulePianoNoteAt
                  : sound === 'bass' ? player!.scheduleBassNoteAt
                  : sound === 'lead' ? player!.scheduleLeadNoteAt
                  : player!.scheduleNoteAt;
                if (expressive) schedule.call(player, midi, when, duration, expression);
                else schedule.call(player, midi, when, duration);
              }
            }
          }, SCHED_INTERVAL_MS);
        }

        const tick = () => {
          if (superseded()) return;
          const t = elapsed();
          if (t >= endSec) {
            stop();
            return;
          }
          setPlayheadBeat(Math.max(offset, offset + t / spb));
          raf.current = requestAnimationFrame(tick);
        };
        raf.current = requestAnimationFrame(tick);
      };

      if (player) {
        const resumed = player.resume?.() ?? Promise.resolve(false);
        void resumed.then(async (ready) => {
          // stop() may have run while we waited; don't restart a dead transport.
          if (!soundingRef.current || playRequestRef.current !== request) return;
          if (!ready) {
            finish(false);
            return;
          }
          const stillOwned = () =>
            soundingRef.current &&
            playRequestRef.current === request &&
            player!.getPlaybackGeneration() === claimedGeneration;
          if (!stillOwned()) {
            finish(false);
            return;
          }
          await Promise.all([
            player!.preloadInstrumentSamples(),
            ...(events.some((event) => event.sound === 'piano')
              ? [player!.preloadPianoSamples()]
              : []),
          ]);
          // Loading samples can outlive Stop, a new preview, or another
          // transport claiming the singleton. Only this request may start it.
          if (playRequestRef.current !== request || !soundingRef.current) return;
          const ownerGeneration = player!.getPlaybackGeneration();
          if (ownerGeneration !== claimedGeneration) {
            finish(false);
            return;
          }
          ownerGenerationRef.current = ownerGeneration;
          begin(player!.now() + 0.1, ownerGeneration);
        });
      } else {
        if (playRequestRef.current === request) begin(0, null);
      }
    },
    [timeline, bpm, finish, stop],
  );

  const play = useCallback(
    (requested?: ScoreAuditionRange) => {
      const range = normalizeAuditionRange(timeline.totalBeats, requested);
      if (range) startFrom(range.fromBeat, range);
      else stop();
    },
    [timeline.totalBeats, startFrom, stop],
  );
  const pause = useCallback(() => {
    if (transportRef.current !== 'playing') return;
    const owner = ownerGenerationRef.current;
    if (owner !== null && getChordPlayer().getPlaybackGeneration() !== owner) {
      finish(false);
      return;
    }
    const beat = readBeatRef.current?.() ?? 0;
    const range = rangeRef.current;
    finish(true);
    if (!range || beat >= range.toBeat) return;
    rangeRef.current = range;
    pausedBeatRef.current = beat;
    transportRef.current = 'paused';
    setTransport('paused');
    setPlayheadBeat(beat);
  }, [finish]);
  const resume = useCallback(() => {
    if (transportRef.current === 'paused' && rangeRef.current) {
      startFrom(pausedBeatRef.current, rangeRef.current);
    }
  }, [startFrom]);

  // A score edit/import or tempo change invalidates the scheduler's captured
  // timeline. Stop cleanly instead of continuing to dispatch the old score.
  useEffect(() => {
    const changed = sourceRef.current.timeline !== timeline || sourceRef.current.bpm !== bpm;
    sourceRef.current = { timeline, bpm };
    if (changed && transportRef.current !== 'idle') stop();
  }, [timeline, bpm, stop]);

  useEffect(() => stop, [stop]);

  return { playing: transport === 'playing', transport, playheadBeat, play, pause, resume, stop };
}
